import { Button, Flex, HStack, Heading, Menu, MenuItem } from "@chakra-ui/react"
import {
  UserSpaceMembershipFragment,
  useUserSpaceMembershipsQuery
} from "@pathwright/graphql-client/src/generated"
import { flattenEdges, getIsLocalDev } from "@pathwright/utils"
import { getDevModeJWTSync } from "@pathwright/utils/src/dev/dev-mode-token"
import { getAuthToken } from "@pathwright/utils/src/token/utils"
import Link from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { MouseEvent, useMemo } from "react"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { canAdministrate } from "../../user/permissions"
import { SpaceIdentityWithName } from "../identity/SpaceIdentity"

// Navigating here directly authenticates with the Django session cookie, which
// a localhost client never has. In local dev, use `switchSpace` or
// `handleSwitchSpaceClick`, which authenticate with the auth token instead.
export function getSwitchSpaceLink(spaceId: number, path?: string) {
  return buildSwitchSpaceUrl(`/auth/switch-space/${spaceId}/`, spaceId, path)
}

function buildSwitchSpaceUrl(route: string, spaceId: number, path?: string) {
  // Use locally stored school url in local dev mode.
  const origin = getIsLocalDev()
    ? localStorage.getItem("school_config_url")?.slice(0, -1) // Remove trailing slash
    : window.location.origin

  // Setup url to switch spaces.
  let switchSpaceLink = `${origin}${route}`

  const searchParams = new URLSearchParams()

  // Optionally append the path query param for which path to redirect to on the target space.
  let pathname = path ?? window.location.pathname
  if (pathname) {
    searchParams.set("path", pathname)
  }

  // When in local dev, we must supply the url as the ?school query param.
  if (getIsLocalDev()) {
    // Must supply the school ID on the ?path query param.
    searchParams.set("path", `${pathname}?school=${spaceId}`)

    // Supply the local host origin as the ?origin query param.
    searchParams.set("origin", window.location.origin)

    // Include the dev mode token for local dev authentication.
    searchParams.set("devmode", getDevModeJWTSync()!.toString())
  }

  return `${switchSpaceLink}?${searchParams.toString()}`
}

// Asks Django for the SSO link with the auth token rather than the session
// cookie. Falls back to the session-based link if that request fails.
async function getSwitchSpaceSSOLink(spaceId: number, path?: string) {
  const headers: Record<string, string> = {
    Accept: "application/json",
    // Lets non-DEBUG Django echo the localhost origin for CORS.
    "X-Pathwright-Application-Version": "1.0"
  }
  const token = getAuthToken()
  if (token) headers.Authorization = `Token ${token}`

  try {
    const response = await fetch(
      buildSwitchSpaceUrl(`/auth/switch-space/${spaceId}/link/`, spaceId, path),
      { headers }
    )
    if (response.ok) {
      const { url } = await response.json()
      if (url) return url as string
    }
  } catch {}
  return getSwitchSpaceLink(spaceId, path)
}

// In production the client shares the school's domain, so the session cookie
// is present and the plain link works.
export async function switchSpace(
  spaceId: number,
  path?: string,
  target: "_self" | "_blank" = "_self"
) {
  const link = getIsLocalDev()
    ? await getSwitchSpaceSSOLink(spaceId, path)
    : getSwitchSpaceLink(spaceId, path)
  window.open(link, target)
}

// Click handler for links whose href is `getSwitchSpaceLink(spaceId, path)`.
// Only intervenes in local dev, where the href can't authenticate.
export function handleSwitchSpaceClick(spaceId: number, path?: string) {
  return (e: MouseEvent) => {
    if (!getIsLocalDev()) return
    e.preventDefault()
    const newTab = e.metaKey || e.ctrlKey || e.shiftKey
    switchSpace(spaceId, path, newTab ? "_blank" : "_self")
  }
}

const SpaceSwitcher = () => {
  const pwContext = usePathwrightContext()
  const { school, me } = pwContext

  const userSpaceMembershipsQuery = useUserSpaceMembershipsQuery()
  const memberships = (flattenEdges(
    userSpaceMembershipsQuery?.data?.me?.memberships
  ) || []) as UserSpaceMembershipFragment[]

  // Sorting memberships based on when the user was last seen (based on heartbeat).
  const sortedMemberships = useMemo(() => {
    // Filter out current school and sort by last seen date descending.
    return memberships
      .filter((membership) => membership.school.id !== school.id)
      .sort((a, b) => {
        return (
          new Date(b.last_seen || 0).getTime() -
          new Date(a.last_seen || 0).getTime()
        )
      })
  }, [memberships])

  if (!me || (sortedMemberships.length === 0 && !canAdministrate(pwContext))) {
    return <SpaceIdentityWithName space={school} p=".5em" px=".75em" />
  }

  return (
    <Menu.Root size="md" lazyMount positioning={{ sameWidth: true }}>
      <Menu.Trigger asChild>
        <Button
          variant="ghost"
          colorPalette="blackAlpha"
          size="md"
          p=".5em"
          px=".75em"
          w="full"
        >
          <SpaceIdentityWithName space={school} />
          <Pathicon icon="triangles" />
        </Button>
      </Menu.Trigger>
      <Menu.Positioner>
        <Menu.Content maxH="200px">
          {sortedMemberships.map((membership) => (
            <Menu.Item
              asChild
              key={membership.school.id}
              value={membership.school.id + ""}
            >
              <Link
                href={getSwitchSpaceLink(membership.school.id)}
                target="_self"
                onClick={handleSwitchSpaceClick(membership.school.id)}
              >
                <SpaceIdentityWithName space={membership.school} />
              </Link>
            </Menu.Item>
          ))}
          {canAdministrate(pwContext) && (
            <MenuItem asChild value="new-space">
              <Link
                href={`https://paths.pathwright.com/sign-up/?utm_campaign=space-switcher&utm_medium=platform&utm_content=school-${school.subdomain}-${school.id}`}
                variant="plain"
                textDecor="none"
              >
                <HStack w="full">
                  <Flex align="center" justify="center" w="2rem" h="2rem">
                    <Pathicon
                      icon="plus-circle-fill"
                      color="blackAlpha.700"
                      size="1.5rem"
                    />
                  </Flex>
                  <Heading as="h2" size="sm" m={0}>
                    New Pathwright Space
                  </Heading>
                </HStack>
              </Link>
            </MenuItem>
          )}
        </Menu.Content>
      </Menu.Positioner>
    </Menu.Root>
  )
}

export default SpaceSwitcher
